import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useBranding } from '../context/BrandingContext';
import { api, getApiBaseUrl } from '../api/client';
import EventMarquee from '../components/EventMarquee';
import ActivityCard from '../components/courses/ActivityCard';
import ActivityDetailModal from '../components/courses/ActivityDetailModal';
import { Button, EmptyState, SkeletonCard } from '../components/ui';
import type { IActivityWithCourses, ICourse } from '../utils/courseSchedule';
import './TenantWelcomePage.css';

interface IPublicActivityPreview {
  _id: number;
  name: string;
  description: string;
  joinPolicy: string;
  coverImage?: string | null;
  courses?: ICourse[];
}

/** Two whole rows on desktop, and a mobile section that ends. */
const ACTIVITIES_PREVIEW_LIMIT = 4;

const getAssetUrl = (path: string | undefined | null): string | null => {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (path.startsWith('/uploads')) return `${getApiBaseUrl()}${path}`;
  return path;
};

const TenantWelcomePage: React.FC = () => {
  const { t } = useTranslation();
  const { organizationName, welcomePage, allowSelfRegistration } = useBranding();

  const title = welcomePage?.title || (organizationName
    ? t('welcomePage.defaultTitle', { name: organizationName })
    : t('welcomePage.welcome'));
  const description = welcomePage?.description || '';

  // Two photos with two jobs. The background is the establishing shot and
  // belongs in the masthead, which is a wide shallow band; the banner is a
  // subject photo and reads better at 4:3 beside the text in "about us".
  const bannerImage = getAssetUrl(welcomePage?.bannerImage);
  const heroImage = getAssetUrl(welcomePage?.backgroundImage) ?? bannerImage;
  const featureImage = bannerImage && bannerImage !== heroImage ? bannerImage : null;
  // Was backgroundOpacity: now how deeply the photo dissolves into the band.
  const mediaFade = typeof welcomePage?.backgroundOpacity === 'number'
    ? Math.max(0, Math.min(1, welcomePage.backgroundOpacity))
    : 0.5;
  const showMarquee = welcomePage?.showEventMarquee !== false;

  const [activities, setActivities] = useState<IPublicActivityPreview[] | null>(null);
  const [activitiesFailed, setActivitiesFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedActivity, setSelectedActivity] = useState<IActivityWithCourses | null>(null);

  useEffect(() => {
    let cancelled = false;
    setActivities(null);
    setActivitiesFailed(false);
    api.activities.listPublic()
      .then(res => {
        if (cancelled) return;
        setActivities((res.data as IPublicActivityPreview[]) || []);
      })
      .catch(() => {
        // Distinct from empty: an empty list hides the section, so folding a
        // failure into it would make an outage invisible.
        if (!cancelled) {
          setActivities([]);
          setActivitiesFailed(true);
        }
      });
    return () => { cancelled = true; };
  }, [reloadKey]);

  const loadingActivities = activities === null;
  const totalActivities = activities?.length ?? 0;
  const previewActivities = (activities || []).slice(0, ACTIVITIES_PREVIEW_LIMIT);
  const hasMoreActivities = totalActivities > ACTIVITIES_PREVIEW_LIMIT;
  const showActivities = loadingActivities || activitiesFailed || totalActivities > 0;

  return (
    <div className="welcome-page">
      <header className="welcome-mast">
        {heroImage && (
          <div
            className="welcome-mast__media"
            style={{
              '--hero-media-fade': mediaFade,
              backgroundImage: `url(${heroImage})`,
            } as React.CSSProperties}
          >
            <img src={heroImage} alt="" className="welcome-mast__photo" />
          </div>
        )}
        <div className={`welcome-mast__band${heroImage ? '' : ' welcome-mast__band--alone'}`}>
          {/* No logo here: the app header already shows the tenant's logo
              directly above. Repeating it turned the band into a second
              navbar — same logo left, same buttons right. */}
          <div className="welcome-mast__text">
            <h1 className="welcome-mast__title">{title}</h1>
          </div>
          <div className="welcome-mast__actions">
            <Button as="link" to="/activities" variant="primary">
              {t('welcomePage.browseActivitiesCta')}
            </Button>
            <Button as="link" to="/login" variant="secondary">
              {t('welcomePage.login')}
            </Button>
          </div>
        </div>
      </header>

      <div className="welcome-page__wrap">
        {showMarquee && (
          <section className="welcome-section welcome-section--events">
            <EventMarquee seeAllHref="/open-events" seeAllLabel={t('welcomePage.seeAllEvents')} />
          </section>
        )}

        {showActivities && (
          <section
            className="welcome-section"
            aria-labelledby="welcome-activities-title"
            aria-busy={loadingActivities}
          >
            <div className="welcome-section__head">
              <h2 id="welcome-activities-title" className="welcome-section__title">
                {t('welcomePage.activitiesHeading')}
              </h2>
            </div>

            {loadingActivities ? (
              <div className="activities-poster-grid">
                <SkeletonCard />
                <SkeletonCard />
              </div>
            ) : activitiesFailed ? (
              <EmptyState
                size="sm"
                title={t('welcomePage.activitiesLoadError')}
                action={{ label: t('common.retry'), onClick: () => setReloadKey(k => k + 1) }}
              />
            ) : (
              <>
                <div className="activities-poster-grid">
                  {previewActivities.map(activity => (
                    <ActivityCard
                      key={activity._id}
                      activity={{
                        _id: activity._id,
                        name: activity.name,
                        description: activity.description,
                        coverImage: activity.coverImage,
                        joinPolicy: activity.joinPolicy,
                        courses: activity.courses || [],
                      }}
                      onSelect={setSelectedActivity}
                    />
                  ))}
                </div>
                {/* Under the grid, not in the header: four cards with nothing
                    after them read as "there are only four". */}
                <Link to="/activities" className="welcome-activities__cta">
                  {hasMoreActivities
                    ? t('welcomePage.seeAllActivitiesCount', { count: totalActivities })
                    : t('welcomePage.seeAllActivities')}
                </Link>
              </>
            )}
          </section>
        )}

        {(description || featureImage) && (
          <section className="welcome-section" aria-labelledby="welcome-about-title">
            <div className="welcome-section__head">
              <h2 id="welcome-about-title" className="welcome-section__title">
                {t('welcomePage.aboutHeading')}
              </h2>
            </div>
            <div className={`welcome-about${featureImage ? ' welcome-about--with-photo' : ''}`}>
              {featureImage && (
                <img src={featureImage} alt="" className="welcome-about__photo" />
              )}
              {description && (
                <div className="welcome-about__text">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {description}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          </section>
        )}

        {allowSelfRegistration && (
          <div className="welcome-page__signup">
            <Button as="link" to="/register" variant="primary">
              {t('welcomePage.register')}
            </Button>
          </div>
        )}
      </div>

      <ActivityDetailModal activity={selectedActivity} onClose={() => setSelectedActivity(null)} />
    </div>
  );
};

export default TenantWelcomePage;
